import { useState } from "react";
import GpsOdometer from "@/components/GpsOdometer";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import PhotoUploader from "@/components/PhotoUploader";
import SignaturePad from "@/components/SignaturePad";
import InventoryChecklist from "@/components/InventoryChecklist";
import { defaultInventoryState, FUEL_LEVELS } from "@/lib/inventory";
import { toast } from "sonner";

/**
 * Owner-side return registration: the owner walks the vehicle with the
 * client at handback and registers the return from the admin panel
 * (mirrors the client checkout wizard in a single form).
 */
export default function AdminCheckoutForm({ reservation, onSaved }) {
  const kmStart = reservation.handover_data?.km_start;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    km_end: "",
    fuel_level: "lleno",
    interior_clean: true,
    exterior_clean: true,
    black_water_emptied: true,
    grey_water_emptied: true,
    fresh_water_filled: true,
    returned_on_time: true,
    photos: [],
    incidents: "",
    damages: "",
    inventory: defaultInventoryState(),
    signature: "",
    owner_signature: "",
  });
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const valid = () => {
    if (data.km_end === "" || Number(data.km_end) < 0) return "Falta el kilometraje de llegada";
    if (kmStart != null && Number(data.km_end) < Number(kmStart))
      return `El km de llegada no puede ser menor que el de salida (${kmStart})`;
    if (data.photos.length < 2) return "Añade al menos 2 fotos del estado de devolución";
    if (!data.signature) return "Falta la firma del viajero";
    if (!data.owner_signature) return "Falta la firma del propietario";
    return null;
  };

  const submit = async () => {
    const err = valid();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const { data: resp } = await api.post(`/reservations/${reservation.id}/checkout`, {
        ...data,
        km_end: Number(data.km_end),
        km_start: null, // el backend usa el km de la entrega como fuente de verdad
      });
      if (resp.extra_charge > 0) {
        toast.warning(`Devolución registrada. Cargo por exceso de km: ${resp.extra_charge.toFixed(2)} € (se aplica en la Liquidación)`);
      } else {
        toast.success("Devolución registrada. Ya puedes liquidar la fianza.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-checkout-form">
      <div className="bg-white border border-sand rounded-xl p-6">
        <h3 className="font-display text-lg font-semibold text-charcoal mb-1">
          Registrar devolución (propietario)
        </h3>
        <p className="text-sm text-warmgray mb-4">
          Registra el estado de devolución junto al cliente. El cliente firma
          en tu dispositivo; después podrás liquidar la fianza en la pestaña
          Liquidación.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div>
            <Label>Km a la salida</Label>
            <Input value={kmStart ?? "—"} disabled className="mt-2 bg-sand-light border-sand" data-testid="aco-km-start" />
          </div>
          <div>
            <Label>Km a la devolución</Label>
            <Input type="number" min="0" value={data.km_end} onChange={(e) => set("km_end", e.target.value)} className="mt-2 bg-ivory border-sand" data-testid="aco-km-end" />
            <GpsOdometer onValue={(km) => set("km_end", String(km))} onFuel={(f) => set("fuel_level", f)} current={data.km_end} testId="aco-gps" />
          </div>
          <div>
            <Label>Combustible</Label>
            <Select value={data.fuel_level} onValueChange={(v) => set("fuel_level", v)}>
              <SelectTrigger className="mt-2 bg-ivory border-sand" data-testid="aco-fuel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FUEL_LEVELS.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {[
            ["interior_clean", "Interior limpio"],
            ["exterior_clean", "Exterior limpio"],
            ["black_water_emptied", "Aguas negras (WC) vaciadas"],
            ["grey_water_emptied", "Aguas grises vaciadas"],
            ["fresh_water_filled", "Depósito de agua llenado"],
            ["returned_on_time", "Devuelto a la hora acordada"],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center justify-between bg-ivory border border-sand rounded-lg px-4 py-3 cursor-pointer">
              <span className="text-sm text-charcoal">{label}</span>
              <Checkbox checked={data[key]} onCheckedChange={(v) => set(key, Boolean(v))} data-testid={`aco-${key}`} />
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white border border-sand rounded-xl p-6">
        <h3 className="font-display text-lg font-semibold text-charcoal mb-3">Fotos de la devolución (mínimo 2)</h3>
        <PhotoUploader
          label={`Fotos (${data.photos.length}/2+)`}
          value={data.photos}
          onChange={(v) => set("photos", v)}
          multiple
          adminUpload
          testid="aco-photos"
        />
      </div>

      <div className="bg-white border border-sand rounded-xl p-6 space-y-4">
        <h3 className="font-display text-lg font-semibold text-charcoal">Incidencias y daños</h3>
        <div>
          <Label>Incidencias durante el viaje</Label>
          <Textarea value={data.incidents} onChange={(e) => set("incidents", e.target.value)} rows={2} className="mt-2 bg-ivory border-sand" data-testid="aco-incidents" />
        </div>
        <div>
          <Label>Daños o golpes nuevos detectados</Label>
          <Textarea value={data.damages} onChange={(e) => set("damages", e.target.value)} rows={2} className="mt-2 bg-ivory border-sand" data-testid="aco-damages" />
        </div>
      </div>

      <div className="bg-white border border-sand rounded-xl p-6">
        <h3 className="font-display text-lg font-semibold text-charcoal mb-3">Inventario devuelto</h3>
        <InventoryChecklist value={data.inventory} onChange={(v) => set("inventory", v)} />
      </div>

      <div className="bg-white border border-sand rounded-xl p-6">
        <Label>Firma del viajero ({reservation.client_name})</Label>
        <div className="mt-2 max-w-md">
          <SignaturePad value={data.signature} onChange={(v) => set("signature", v)} testid="aco-signature" />
        </div>
        <div className="mt-5">
          <Label>Firma del propietario (CamperVan.cat)</Label>
          <div className="mt-2 max-w-md">
            <SignaturePad value={data.owner_signature} onChange={(v) => set("owner_signature", v)} testid="aco-owner-signature" />
          </div>
        </div>
      </div>

      <Button
        onClick={submit}
        disabled={loading}
        className="w-full bg-terracotta hover:bg-terracotta-dark text-white h-12 text-base"
        data-testid="aco-submit"
      >
        {loading ? "Registrando..." : "Registrar devolución"}
      </Button>
    </div>
  );
}
